import { useCallback, useRef } from 'react'
import type { Paragraph } from '@/api/types/stowed-element'
import { paragraphsToPlainText } from '@/lib/shapes/shape-text'
import type { ShapeTextParagraphsChangeOptions } from './use-shape-text-editing'

/** The single path for editor-originated paragraph writes, plus the provenance it records. */
export function useParagraphEmitter(
  onParagraphsChange: (
    shapeId: string,
    paragraphs: Paragraph[],
    options?: ShapeTextParagraphsChangeOptions
  ) => void
) {
  /**
   * Plain text of the paragraphs this editor itself last pushed to the model.
   *
   * The provenance signal for the trim-resync (`use-chain-trim-resync.ts`): anything the editor
   * wrote round-trips equal to this, so a `paragraphs` prop that differs from it can only have come
   * from somewhere else — in practice a chain reflow. Comparing against `textValue` instead would be
   * direction-biased and would also mistake any normalisation inside `applyEditToParagraphs` for an
   * external write.
   */
  const lastEmittedModelTextRef = useRef<string | null>(null)

  /**
   * Monotonic count of editor-originated writes. It is the "a real edit happened" half of the
   * hand-off dedupe signature (`use-chain-overflow-handoff.ts`) — see `lastOverflowAttemptRef` there
   * for why the text-derived half alone stopped being enough once the chain began flowing per
   * keystroke.
   */
  const emitSeqRef = useRef(0)

  /** Every editor-originated paragraph write goes through here so the ref can never drift. */
  const emitParagraphs = useCallback(
    (shapeId: string, next: Paragraph[], options?: ShapeTextParagraphsChangeOptions) => {
      lastEmittedModelTextRef.current = paragraphsToPlainText(next)
      emitSeqRef.current += 1
      // Forwarded only when present, so the two-argument call shape survives for every caller with
      // nothing to add — see `ShapeTextParagraphsChangeOptions`.
      if (options) onParagraphsChange(shapeId, next, options)
      else onParagraphsChange(shapeId, next)
    },
    [onParagraphsChange]
  )

  return { lastEmittedModelTextRef, emitSeqRef, emitParagraphs }
}
